import { useCallback, useState } from "react";
import { API_BASE_URL } from "../constants/api";
import {
  EVENT_ACTION_PP_CHANGE_VERSION,
  EVENT_CATEGORY_PRIVACY_POLICY,
} from "../constants/analytics";
import { GPPJsonConfig, VersionHistoryState } from "../types/version-history";
import Http from "../services/http";
import GoogleAnalytics from "../utils/GoogleAnalytics";
import {
  PrivacyPolicyTabType,
  buildPPSourcePath,
  buildVersionHistoryPath,
  determineFolderType,
  hasPrivacyPolicyForLanguage,
  parseLanguageString,
} from "../utils/privacyPolicyUtils";

// Module-level cache for version history responses to avoid re-fetching on tab switches.
// Placed at module level so it persists across component re-mounts (e.g., React.StrictMode)
const versionHistoryCache = new Map<string, GPPJsonConfig>();

/**
 * Clear the version history cache (useful for testing)
 */
export const clearVersionHistoryCache = () => versionHistoryCache.clear();

interface UsePrivacyPolicyVersionHistoryReturn {
  versionHistories: VersionHistoryState[];
  hasVersionHistory: boolean;
  selectedHistory: VersionHistoryState | undefined;
  ppSource: string;
  handleSelectHistory: (history: VersionHistoryState) => Promise<void>;
  loadPrivacyPolicyVersionHistory: (
    countryCode: string,
    langCode: string,
    tabType?: PrivacyPolicyTabType
  ) => Promise<void>;
}

/**
 * Custom hook for managing privacy policy version history
 * Handles version history loading, selection, and source file management
 */
export const usePrivacyPolicyVersionHistory = (
  language: string,
  activeTabType: PrivacyPolicyTabType,
  isEU: boolean
): UsePrivacyPolicyVersionHistoryReturn => {
  const [ppSource, setPpSource] = useState(
    `${API_BASE_URL}/privacy_policy/en.html`
  );
  const [hasVersionHistory, setHasVersionHistory] = useState(false);
  const [versionHistories, setVersionHistories] = useState<
    VersionHistoryState[]
  >([]);
  const [selectedHistory, setSelectedHistory] = useState<VersionHistoryState>();

  /**
   * Set the privacy policy source file based on date, language, and tab type
   */
  const setPPsourceFile = useCallback(
    async (date: string, lang: string, tabType?: PrivacyPolicyTabType) => {
      const [langCode, countryCode] = parseLanguageString(lang);
      const ppExist = hasPrivacyPolicyForLanguage(lang);
      const folderType = determineFolderType(isEU, tabType);

      const ppSourcePath = buildPPSourcePath(
        countryCode,
        folderType,
        langCode,
        date,
        ppExist,
        isEU
      );

      setPpSource(ppSourcePath);
    },
    [isEU]
  );

  /**
   * Load version history configuration from the server
   */
  const loadPrivacyPolicyVersionHistory = useCallback(
    async (
      countryCode: string,
      langCode: string,
      tabType?: PrivacyPolicyTabType
    ) => {
      try {
        const folderType = determineFolderType(isEU, tabType);
        const url = buildVersionHistoryPath(
          countryCode,
          folderType,
          langCode,
          isEU
        );

        // Check cache first to avoid redundant fetches on tab switches
        const cachedResponse = versionHistoryCache.get(url);
        const response: GPPJsonConfig = cachedResponse || (await Http.get(url));

        // Cache the response if it was freshly fetched
        if (!cachedResponse) {
          versionHistoryCache.set(url, response);
        }

        // Filter out draft versions and sort by display date descending
        const publishedVersions = response.versions
          .filter((version) => version.status !== "DRAFT")
          .sort(
            (a, b) =>
              new Date(b.displayDate).getTime() -
              new Date(a.displayDate).getTime()
          );

        // Determine if version history should be shown
        const shouldShowVersionHistory =
          response.enabled && publishedVersions.length > 1;

        setHasVersionHistory(shouldShowVersionHistory);

        if (shouldShowVersionHistory) {
          setVersionHistories(publishedVersions);

          // Set default selected history
          const defaultHistory =
            publishedVersions.find(
              (version) => version.name === response.current
            ) || publishedVersions[0];

          setSelectedHistory(defaultHistory);
        } else {
          setVersionHistories([]);
          setSelectedHistory(undefined);
        }

        // Set source file
        if (response.current) {
          await setPPsourceFile(response.current, language, tabType);
        } else {
          // Fallback to global en.html
          const globalGPPURL = `${API_BASE_URL}/privacy_policy/ZZ/en/version_history.json`;
          const gppVersionHistoryResponse: GPPJsonConfig =
            await Http.get(globalGPPURL);
          setPpSource(
            `${API_BASE_URL}/privacy_policy/ZZ/en/${gppVersionHistoryResponse.current}.html`
          );
        }
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        const folderType = determineFolderType(isEU, tabType);
        const url = buildVersionHistoryPath(
          countryCode,
          folderType,
          langCode,
          isEU
        );
        console.error(
          "Failed to load privacy policy config",
          JSON.stringify({
            error: errorMessage,
            sourcePath: url,
            language,
            langCode,
            countryCode,
            tabType: tabType || "N/A",
            folderType,
            isEU,
          })
        );
        setHasVersionHistory(false);
        setVersionHistories([]);
        setSelectedHistory(undefined);
        setPpSource(`${API_BASE_URL}/privacy_policy/en.html`);
      }
    },
    [language, setPPsourceFile, isEU]
  );

  /**
   * Handle version history selection
   */
  const handleSelectHistory = useCallback(
    async (history: VersionHistoryState) => {
      if (hasVersionHistory) {
        setSelectedHistory(history);
        await setPPsourceFile(history.name, language, activeTabType);
        GoogleAnalytics.triggerEvent(
          EVENT_CATEGORY_PRIVACY_POLICY,
          EVENT_ACTION_PP_CHANGE_VERSION
        );
      }
    },
    [hasVersionHistory, language, activeTabType, setPPsourceFile]
  );

  return {
    versionHistories,
    hasVersionHistory,
    selectedHistory,
    ppSource,
    handleSelectHistory,
    loadPrivacyPolicyVersionHistory,
  };
};
